import React, { useState, useEffect } from 'react';
import { Mic, MicOff, X, Check, Loader2 } from 'lucide-react';
import { Dialog, DialogContent, DialogHeader, DialogTitle } from '../ui/dialog';
import { Button } from '../ui/button';
import { useLanguage } from '../../contexts/LanguageContext';
import { useAuth } from '../../contexts/AuthContext';
import { cn } from '../../lib/utils';

const VoiceEntryModal = ({ open, onClose, onSuccess }) => {
    const { t, language } = useLanguage();
    const { authAxios } = useAuth();
    
    const [listening, setListening] = useState(false);
    const [transcript, setTranscript] = useState('');
    const [parsed, setParsed] = useState(null);
    const [loading, setLoading] = useState(false);
    const [error, setError] = useState('');
    
    // Speech recognition setup
    const [recognition, setRecognition] = useState(null);

    useEffect(() => {
        if ('webkitSpeechRecognition' in window || 'SpeechRecognition' in window) {
            const SpeechRecognition = window.SpeechRecognition || window.webkitSpeechRecognition;
            const rec = new SpeechRecognition();
            rec.continuous = false;
            rec.interimResults = true;
            rec.lang = language === 'hi' ? 'hi-IN' : 'en-IN';
            
            rec.onresult = (event) => {
                const current = event.results[event.results.length - 1];
                setTranscript(current[0].transcript);
            };
            
            rec.onend = () => {
                setListening(false);
            };
            
            rec.onerror = (event) => {
                setError(language === 'en' ? 'Speech recognition error' : 'आवाज़ पहचान में त्रुटि');
                setListening(false);
            };
            
            setRecognition(rec);
        }
        
        return () => {
            if (recognition) {
                recognition.stop();
            }
        };
    }, [language]);

    const toggleListening = () => {
        if (!recognition) {
            setError(language === 'en' ? 'Speech recognition not supported' : 'आवाज़ पहचान समर्थित नहीं है');
            return;
        }
        
        if (listening) {
            recognition.stop();
        } else {
            setTranscript('');
            setParsed(null);
            setError('');
            recognition.start();
            setListening(true);
        }
    };

    const parseVoiceCommand = async () => {
        if (!transcript.trim()) return;
        
        setLoading(true);
        setError('');
        
        try {
            const response = await authAxios.post('/voice/parse', {
                text: transcript,
                language
            });
            setParsed(response.data);
        } catch (err) {
            setError(err.response?.data?.detail || 'Failed to parse command');
        } finally {
            setLoading(false);
        }
    };

    const confirmTransaction = async () => {
        if (!parsed?.parsed) return;
        
        setLoading(true);
        
        try {
            await authAxios.post('/transactions', parsed.parsed);
            onSuccess?.(parsed.parsed);
            handleClose();
        } catch (err) {
            setError(err.response?.data?.detail || 'Failed to save transaction');
        } finally {
            setLoading(false);
        }
    };

    const handleClose = () => {
        setTranscript('');
        setParsed(null);
        setError('');
        setListening(false);
        onClose();
    };

    // Auto-parse when speech ends
    useEffect(() => {
        if (!listening && transcript.trim() && !parsed) {
            parseVoiceCommand();
        }
    }, [listening, transcript]);

    return (
        <Dialog open={open} onOpenChange={handleClose}>
            <DialogContent className="max-w-md mx-auto" data-testid="voice-modal">
                <DialogHeader>
                    <DialogTitle className="text-center flex items-center justify-center gap-2">
                        <Mic className="w-5 h-5" />
                        {t('voiceEntry')}
                    </DialogTitle>
                </DialogHeader>

                <div className="flex flex-col items-center py-6 space-y-6">
                    {/* Voice Wave Animation */}
                    <div className={cn(
                        "voice-wave transition-opacity",
                        listening ? "opacity-100" : "opacity-30"
                    )}>
                        <div className="voice-wave-bar"></div>
                        <div className="voice-wave-bar"></div>
                        <div className="voice-wave-bar"></div>
                        <div className="voice-wave-bar"></div>
                        <div className="voice-wave-bar"></div>
                    </div>

                    {/* Mic Button */}
                    <button
                        onClick={toggleListening}
                        data-testid="voice-mic-btn"
                        className={cn(
                            "w-20 h-20 rounded-full flex items-center justify-center transition-all",
                            listening 
                                ? "bg-rose-500 text-white shadow-lg shadow-rose-500/40 scale-110" 
                                : "bg-primary text-white shadow-lg shadow-primary/40 hover:scale-105"
                        )}
                    >
                        {listening ? (
                            <MicOff className="w-8 h-8" />
                        ) : (
                            <Mic className="w-8 h-8" />
                        )}
                    </button>
                    
                    <p className="text-muted-foreground text-sm">
                        {listening ? t('listening') : t('tapToSpeak')}
                    </p>

                    {/* Transcript */}
                    {transcript && (
                        <div className="w-full p-4 bg-muted rounded-xl" data-testid="voice-transcript">
                            <p className="text-sm text-muted-foreground mb-1">
                                {language === 'en' ? 'You said:' : 'आपने कहा:'}
                            </p>
                            <p className="text-foreground font-medium">{transcript}</p>
                        </div>
                    )}

                    {/* Loading */}
                    {loading && (
                        <div className="flex items-center gap-2 text-muted-foreground">
                            <Loader2 className="w-5 h-5 animate-spin" />
                            <span>{language === 'en' ? 'Processing...' : 'प्रोसेस हो रहा है...'}</span>
                        </div>
                    )}

                    {/* Error */}
                    {error && (
                        <div className="w-full p-3 bg-destructive/10 text-destructive rounded-lg text-sm">
                            {error}
                        </div>
                    )}

                    {/* Parsed Result */}
                    {parsed && (
                        <div className="w-full p-4 bg-card border rounded-xl space-y-3" data-testid="voice-parsed">
                            <p className="text-sm font-medium">
                                {language === 'en' ? parsed.confirmation_en : parsed.confirmation_hi}
                            </p>
                            
                            <div className="flex gap-3">
                                <div className={cn(
                                    "flex-1 p-3 rounded-lg text-center",
                                    parsed.parsed.type === 'sale' 
                                        ? "bg-emerald-100 dark:bg-emerald-900/30 text-emerald-700 dark:text-emerald-400"
                                        : "bg-rose-100 dark:bg-rose-900/30 text-rose-700 dark:text-rose-400"
                                )}>
                                    <p className="text-xs uppercase">{parsed.parsed.type}</p>
                                    <p className="text-lg font-bold font-number">₹{parsed.parsed.amount}</p>
                                </div>
                                <div className="flex-1 p-3 rounded-lg bg-muted text-center">
                                    <p className="text-xs uppercase text-muted-foreground">{t('category')}</p>
                                    <p className="font-medium">{parsed.parsed.category}</p>
                                </div>
                            </div>

                            <div className="flex gap-2 pt-2">
                                <Button
                                    variant="outline"
                                    onClick={() => {
                                        setParsed(null);
                                        setTranscript('');
                                    }}
                                    className="flex-1"
                                    data-testid="voice-retry-btn"
                                >
                                    <X className="w-4 h-4 mr-2" />
                                    {t('cancel')}
                                </Button>
                                <Button
                                    onClick={confirmTransaction}
                                    disabled={loading}
                                    className="flex-1"
                                    data-testid="voice-confirm-btn"
                                >
                                    {loading ? (
                                        <Loader2 className="w-4 h-4 mr-2 animate-spin" />
                                    ) : (
                                        <Check className="w-4 h-4 mr-2" />
                                    )}
                                    {t('confirm')}
                                </Button>
                            </div>
                        </div>
                    )}

                    {/* Examples */}
                    {!transcript && !parsed && (
                        <div className="text-center text-sm text-muted-foreground">
                            <p className="font-medium mb-2">
                                {language === 'en' ? 'Try saying:' : 'बोलकर देखें:'}
                            </p>
                            <div className="space-y-1">
                                <p>"Add sale 450 on milk"</p>
                                <p>"दूध पर 450 की बिक्री जोड़ें"</p>
                                <p>"Expense 300 on stock"</p>
                            </div>
                        </div>
                    )}
                </div>
            </DialogContent>
        </Dialog>
    );
};

export default VoiceEntryModal;
